import { useCallback, useRef } from 'react';
import { randomUuid } from '../../utils/randomUuid';
import {
  extractInlineChipsFromAssistantText,
  pushAssistantMessage,
  pushErrorAssistantMessage,
} from './useAiChatMessageAppend';

/**
 * Handles the voice-to-text upload pipeline.
 * Called by AiFloatingPanel via useVoiceRecorder.onComplete.
 */
export default function useAiAssistantVoice({
  role,
  userName,
  locale,
  voiceMessageLabel,
  detectContext,
  setMessages,
  setLoading,
  startStream,
  abortStream,
  setErrorFromException,
  clearError,
}) {
  // A1: monotonic counter replaces object-identity token — survives overlapping uploads
  const voiceUploadSeqRef = useRef(0);

  const abortVoiceUpload = useCallback(() => {
    // Bump the counter to invalidate all in-flight uploads
    voiceUploadSeqRef.current += 1;
    // If startStream has an in-flight request, abortStream() covers it
    abortStream?.();
  }, [abortStream]);

  const uploadVoiceBlob = useCallback(async (blob, mimeType) => {
    const userMsg = {
      id: randomUuid(),
      role: 'user',
      text: voiceMessageLabel,
      isVoice: true,
      createdAt: Date.now(),
    };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    // A1: capture sequence number at upload start; any bump means we were aborted
    voiceUploadSeqRef.current += 1;
    const mySeq = voiceUploadSeqRef.current;

    try {
      const ext = (mimeType || '').includes('webm') ? 'webm'
        : (mimeType || '').includes('mp4') ? 'm4a' : 'wav';
      const formData = new FormData();
      formData.append('audio', blob, `voice.${ext}`);
      formData.append('context', JSON.stringify({ role, ...detectContext() }));
      formData.append('displayName', userName || '');
      formData.append('locale', locale || 'en');

      clearError?.();
      const result = await startStream(formData);

      // A1: if the sequence counter was bumped, this upload was aborted — discard stale result
      if (voiceUploadSeqRef.current !== mySeq) return;

      if (!result) return; // aborted by startStream's own controller

      const reply = (result.text || '').trim();
      if (!reply) throw new Error('Empty response from assistant');

      if (result.transcript) {
        setMessages(prev => prev.map(m =>
          m === userMsg ? { ...m, transcript: result.transcript } : m,
        ));
      }

      const action = result.action || null;
      const inline_chips = extractInlineChipsFromAssistantText(result.text);
      pushAssistantMessage(setMessages, {
        text: reply,
        action,
        streamContext: result.context || null,
        transcript: result.transcript || null,
        inline_chips,
      });
    } catch (voiceErr) {
      // A1: don't surface error if we were deliberately aborted (seq bumped)
      if (voiceUploadSeqRef.current !== mySeq) return;
      setErrorFromException?.(voiceErr, 'network');
      const detail = voiceErr instanceof Error ? voiceErr.message : String(voiceErr ?? 'Request failed');
      pushErrorAssistantMessage(setMessages, detail);
    } finally {
      setLoading(false);
    }
  }, [role, userName, locale, voiceMessageLabel, detectContext, startStream, setMessages, setLoading, setErrorFromException, clearError]);

  return { uploadVoiceBlob, abortVoiceUpload };
}
